import { Divider, Grid, Typography } from "@mui/material";
import { useEffect, useState, useCallback, useMemo } from "react";
import { ActionButton } from "../../../../components/Button";
import Dialog, { DialogSectionTitle } from "../../../../components/Dialog";
import { IAddEditDialogProps } from "../../../../components/Dialog";
import TextField from "../../../../components/TextField";
import Switch from "../../../../components/Switch";
import Checkbox from "../../../../components/Checkbox";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import IAccessProfile from "../../../../interfaces/IAccessProfile";
import ValidationHelper from '../../../../helpers/ValidationHelper';
import DefaultConstants from "../../../../data/Constants";
import { RegistrationStatusEnum } from "../../../../data/Enums";
import { useEnvironmentContext } from "../../../../contexts/EnvironmentContext";
import IPermission from "../../../../interfaces/IPermission";
import StringHelper from "../../../../helpers/StringHelper";
import useForceRender from "../../../../hooks/useForceRender";
import FormHelper from "../../../../helpers/FormHelper";
import AccessProfileService from '../../../../services/AccessProfileService';
import IPartnerPms from "../../../../interfaces/IPartnerPms";


const AddEditDialog: React.FunctionComponent<IAddEditDialogProps<IAccessProfile>> = ({
    open,
    handleClose,
    data,
    handleListRefresh = () => {}
}) => {
    const forceRender = useForceRender();

    const { translate } = useLanguageContext();
    const { environment } = useEnvironmentContext();

    const [description, setDescription] = useState<string>('');
    const [actived, setActived] = useState<boolean>(true);
    const [permissions, setPermissions] = useState<IPermission[]>([]);


    useEffect(() => {
        if (open) {
            if (data) {
                setDescription(data.description);
                setPermissions(data.permissions);
                setActived(Number(data.registrationStatusId) === RegistrationStatusEnum.Actived);
            } else {
                setDescription('');
                setPermissions([]);
                setActived(true);
            }
        }

        // eslint-disable-next-line
    }, [open]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        let formData = FormHelper.Create(event);
        if (data?.id) {
            formData = FormHelper.Add(formData, 'id', data.id);
        }

        AccessProfileService.AddEdit(formData).then(() => {
            handleClose();
            handleListRefresh();
        });

        // eslint-disable-next-line
    }, [data])


    const handleChangePermissions = useCallback((isChecked: boolean, permissionList: IPermission[]) => {
        const permissionsAux = permissions;

        permissionList.forEach(permission => {
            const index = permissions.findIndex(perm => perm.id === permission.id);

            if (isChecked) {
                if (index === -1) {
                    permissionsAux.push(permission);
                }
            } else {
                if (index !== -1) {
                    permissionsAux.splice(index, 1);
                }
            }
        });

        setPermissions(permissionsAux);
        forceRender();

        // eslint-disable-next-line
    }, [environment, permissions]);
    

    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='cancel' onClick={handleClose} />,
            <ActionButton action='save' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);
    

    return (
        <Dialog
            open={open}
            handleClose={handleClose}
            title={translate(data ? 'edit' : 'add')}
            maxWidth='md'
            fullWidth={true}
            fullHeight={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween'
        >
            <Grid container columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item md={6} xs={12}>
                    <TextField 
                        label={translate('description')} 
                        name='description' 
                        value={description} 
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => ValidationHelper.MaxLength(e.target.value, 100, setDescription)}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <TextField 
                        label={translate('token')}
                        value={data?.token ?? ''} 
                        disabled={true}
                        required={false}
                    />
                </Grid>

                <Grid item xs={12} className='textalign-right'>
                    <Switch
                        label={translate('actived')}
                        name='registrationStatusId'
                        value={actived ? RegistrationStatusEnum.Actived : RegistrationStatusEnum.Inactived}
                        checked={actived}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setActived(e.target.checked)}
                    />
                </Grid>

                <Grid item xs={12} marginY={4}>
                    <Divider />
                </Grid>

                <Grid item xs={12}>
                    <DialogSectionTitle title={translate('permissions')} />

                    {
                        environment.permissionList.filter(p => Number(p.level) === 1).map((p1: IPermission, index1: number) => {
                            const p1_alias = p1.alias;
                            const p1_name = StringHelper.FirstLetterLowerCase(p1_alias);

                            const p2_grid_md = p1.code === '1' ? 4 : 6;
                
                            return <>
                                <Grid item xs={12} marginTop={4} marginBottom={1} key={index1}>
                                    <Typography variant='subtitle1' component='span' className='fontsize-14 fontweight-600' paddingLeft={0.4}>
                                        {environment.partnerPms[`${p1_name}ModuleName` as keyof(IPartnerPms)].toString()}
                                    </Typography>
                                </Grid>
                
                                <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing}>
                                    {
                                        environment.permissionList.filter(p2 => Number(p2.level) === 2 && p2.code.includes(`${p1.code}.`)).map((p2: IPermission, index2: number) => {
                                            const p2_alias = StringHelper.FirstLetterLowerCase(p2.alias.replaceAll(`${p1_alias}.`, ''));
                                            const p2_name = StringHelper.FirstLetterLowerCase(p2_alias);
                    
                                            const p3_list = environment.permissionList.filter(p3 => Number(p3.level) === 3 && p3.code.includes(`${p2.code}.`));
                                            const p3_checked = p3_list.filter(p3 => permissions.some(p => Number(p.id) === Number(p3.id))).length;

                                            const p4_list = environment.permissionList.filter(p4 => Number(p4.level) === 4 && p4.code.includes(`${p2.code}.`));
                                            
                                            const p2_checked = p3_list.length > 0 ? p3_list.every(p3 => permissions.some(p => Number(p.id) === Number(p3.id))) : permissions.some(p => Number(p.id) === Number(p2.id));
                                            const p2_indeterminate = p3_checked > 0 && p3_checked !== p3_list.length;
                    
                                            return <Grid item md={p2_grid_md} sm={6} xs={12} key={index2} alignItems='stretch'>
                                                <Grid item className='permissionbox-grid'>
                                                    <Grid item xs={12}>
                                                        <Checkbox
                                                            label={translate(p2_name)}
                                                            name={p2_checked || p2_indeterminate ? `permissions[${p2.id}]` : ''}
                                                            checked={p2_checked}
                                                            indeterminate={p2_indeterminate}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangePermissions(e.target.checked, p3_list.concat(p4_list).concat(p2))}
                                                        />
                                                    </Grid>
                        
                                                    {
                                                        p3_list.length > 0 && <>
                                                            <Grid item xs={12} marginY={1}>
                                                                <Divider />
                                                            </Grid>

                                                            <Grid item xs={12}>
                                                                <Grid container>
                                                                    {
                                                                        p3_list.map((p3: IPermission, index3: number) => {
                                                                            const p3_name = StringHelper.FirstLetterLowerCase(p3.alias.split('.').pop() as string);

                                                                            const p4_list = environment.permissionList.filter(p4 => Number(p4.level) === 4 && p4.code.includes(`${p3.code}.`));
                                                                            const p4_checked = p4_list.filter(p4 => permissions.some(p => Number(p.id) === Number(p4.id))).length;
                                                                            
                                                                            const p3_checked = p4_list.length > 0 ? p4_list.every(p4 => permissions.some(p => Number(p.id) === Number(p4.id))) : permissions.some(p => Number(p.id) === Number(p3.id));
                                                                            const p3_indeterminate = p4_checked > 0 && p4_checked !== p4_list.length;

                                                                            return <>
                                                                                <Grid item xs={12} key={index3}>
                                                                                    <Checkbox
                                                                                        label={translate(p3_name)}
                                                                                        name={p3_checked || p3_indeterminate ? `permissions[${p3.id}]` : ''}
                                                                                        checked={p3_checked}
                                                                                        indeterminate={p3_indeterminate}
                                                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangePermissions(e.target.checked, p4_list.concat(p3))}
                                                                                    />
                                                                                </Grid>

                                                                                {
                                                                                    p4_list.length > 0 && <>
                                                                                        <Grid item xs={12} className={`indented-grid ${p3_checked ? 'all-checked' : ''}`}>
                                                                                            <Grid container>
                                                                                                {
                                                                                                    p4_list.map((p4: IPermission, index3: number) => {
                                                                                                        const p4_name = StringHelper.FirstLetterLowerCase(p4.alias.split('.').pop() as string);
                                                                                                        const p4_checked = permissions.some(perm => perm.id === p4.id);
                                                                            
                                                                                                        return <>
                                                                                                            <Grid item xs={6} key={index3}>
                                                                                                                <Checkbox
                                                                                                                    label={translate(p4_name)}
                                                                                                                    name={p4_checked ? `permissions[${p4.id}]` : ''}
                                                                                                                    checked={p4_checked}
                                                                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangePermissions(e.target.checked, [p4])}
                                                                                                                />
                                                                                                            </Grid>
                                                                                                        </>;
                                                                                                    })
                                                                                                }
                                                                                            </Grid>
                                                                                        </Grid>
                                                                                    </>
                                                                                }
                                                                            </>;
                                                                        })
                                                                    }
                                                                </Grid>
                                                            </Grid>
                                                        </>
                                                    }
                                                </Grid>
                                            </Grid>;
                                        })
                                    }
                                </Grid>
                            </>;
                        })
                    }
                </Grid>
            </Grid>
        </Dialog>
    );
}

export default AddEditDialog;